import { useCallback, useRef, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import {
	isUsableDate,
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	usePassChangeReasons,
	usePassChanges,
	useProductSizes,
	useProductTypes,
	useStands,
} from "../hooks/usePassChanges";
import type { CatalogOption, PassChangeRow } from "../models/PassChanges";

// The SP returns the Timestamp in UTC; convert to the browser's local time here
// (new SPs never use IH.ToLocal — the frontend handles timezone conversion).
const fmtLocal = (v?: string | null): string =>
	v ? moment.utc(v).local().format("YYYY-MM-DD HH:mm:ss") : "";
const num = (dec: number) => (p: { value: unknown }) =>
	p.value == null || p.value === "" ? "" : Number(p.value).toFixed(dec);

const COLUMN_DEFS: ColDef<PassChangeRow>[] = [
	{
		field: "Timestamp",
		headerName: "Datetime",
		flex: 1.5,
		minWidth: 160,
		valueFormatter: (p) => fmtLocal(p.data?.Timestamp),
	},
	{ field: "StandName", headerName: "Stand", width: 110 },
	{ field: "PrevPass", headerName: "Previous Pass", flex: 1, minWidth: 130 },
	{ field: "Pass", headerName: "Pass", flex: 1, minWidth: 130 },
	{
		field: "PassChangeReasonName",
		headerName: "Reason",
		flex: 1,
		minWidth: 140,
	},
	{
		field: "ScheduledTons",
		headerName: "Sched. Tons",
		width: 120,
		cellStyle: { textAlign: "right" },
		valueFormatter: num(2),
	},
	{
		field: "RolledTons",
		headerName: "Rolled Tons",
		width: 120,
		cellStyle: { textAlign: "right" },
		valueFormatter: num(2),
	},
	{
		field: "AvailablePassesCount",
		headerName: "Avail. Passes",
		width: 120,
		cellStyle: { textAlign: "center" },
	},
	{
		field: "ProductTypeName",
		headerName: "Product Type",
		flex: 1,
		minWidth: 130,
	},
	{
		field: "ProductSizeName",
		headerName: "Product Size",
		flex: 1,
		minWidth: 130,
	},
	{
		field: "CrewId",
		headerName: "Shift",
		width: 80,
		cellStyle: { textAlign: "center" },
	},
	{ field: "Comments", headerName: "Comments", flex: 1.5, minWidth: 160 },
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day").subtract(7, "days")),
	end: urlParam.date(() => moment().startOf("day")),
	standId: urlParam.int(),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	passChangeReasonId: urlParam.int(),
};

export const PassChangesReport = () => {
	const [
		{
			start,
			end,
			standId,
			productTypeId,
			productSizeId,
			passChangeReasonId: reasonId,
		},
		setFilters,
		{ dropValues },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<PassChangeRow> | null>(null);

	const dataQuery = usePassChanges({
		start: start.format("YYYY-MM-DD"),
		end: end.format("YYYY-MM-DD"),
		standId,
		productTypeId,
		productSizeId,
		passChangeReasonId: reasonId,
	});
	const standsQuery = useStands();
	const productTypesQuery = useProductTypes();
	const productSizesQuery = useProductSizes();
	const reasonsQuery = usePassChangeReasons();

	// A shared link can name a stand, product or reason that is no longer offered.
	useDropUnknownValue(
		standId,
		standsQuery.isSuccess,
		(standsQuery.data ?? []).map((o) => o.Id),
		useCallback(() => dropValues("standId"), [dropValues]),
	);
	useDropUnknownValue(
		productTypeId,
		productTypesQuery.isSuccess,
		(productTypesQuery.data ?? []).map((o) => o.Id),
		useCallback(() => dropValues("productTypeId"), [dropValues]),
	);
	useDropUnknownValue(
		productSizeId,
		productSizesQuery.isSuccess,
		(productSizesQuery.data ?? []).map((o) => o.Id),
		useCallback(() => dropValues("productSizeId"), [dropValues]),
	);
	useDropUnknownValue(
		reasonId,
		reasonsQuery.isSuccess,
		(reasonsQuery.data ?? []).map((o) => o.Id),
		useCallback(() => dropValues("passChangeReasonId"), [dropValues]),
	);

	const rows = dataQuery.data ?? [];

	const handleGridReady = useCallback((e: GridReadyEvent<PassChangeRow>) => {
		gridApiRef.current = e.api;
	}, []);

	const handleExportExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({ fileName: "Pass_Changes.xlsx" });
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	const pick = (
		label: string,
		options: CatalogOption[],
		value: number | null,
		onChange: (id: number | null) => void,
	) => (
		<Autocomplete
			size="small"
			sx={{ minWidth: 180 }}
			options={options}
			value={options.find((o) => o.Id === value) ?? null}
			getOptionLabel={(o) => o.Name}
			isOptionEqualToValue={(o, v) => o.Id === v.Id}
			onChange={(_e, option) => onChange(option?.Id ?? null)}
			renderInput={(params) => <TextField {...params} label={label} />}
		/>
	);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Pass Changes
			</Typography>

			<Paper
				elevation={3}
				sx={{
					p: 1.5,
					display: "flex",
					gap: 1.5,
					alignItems: "center",
					flexWrap: "wrap",
				}}
			>
				<DatePicker
					label="Start"
					format="MM/DD/YYYY"
					value={start}
					maxDate={end}
					onChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
				/>
				<DatePicker
					label="End"
					format="MM/DD/YYYY"
					value={end}
					minDate={start}
					onChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
				/>
				{pick("Stand", standsQuery.data ?? [], standId, (id) =>
					setFilters({ standId: id }),
				)}
				{pick(
					"Product Type",
					productTypesQuery.data ?? [],
					productTypeId,
					(id) => setFilters({ productTypeId: id }),
				)}
				{pick(
					"Product Size",
					productSizesQuery.data ?? [],
					productSizeId,
					(id) => setFilters({ productSizeId: id }),
				)}
				{pick("Reason", reasonsQuery.data ?? [], reasonId, (id) =>
					setFilters({ passChangeReasonId: id }),
				)}
				<Box sx={{ flex: 1 }} />
				<Button
					size="small"
					variant="outlined"
					startIcon={<RefreshIcon />}
					onClick={() => dataQuery.refetch()}
					disabled={dataQuery.isFetching}
				>
					Refresh
				</Button>
				<Button
					size="small"
					variant="outlined"
					startIcon={<DownloadIcon />}
					onClick={handleExportExcel}
				>
					Export
				</Button>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<PassChangeRow>
						rowData={rows}
						columnDefs={COLUMN_DEFS}
						defaultColDef={{ resizable: true, sortable: true }}
						suppressContextMenu
						headerHeight={32}
						rowHeight={28}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
